import { useEffect } from "react";
import "@/App.css";
import SiteHeader from "@/components/SiteHeader";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Mail, Phone, MapPin, Flower2, Heart, Home, Sprout, Check } from "lucide-react";

const JANE_URL = "https://hayahealingpsychotherapy.janeapp.com/";
const HEADSHOT = "/momna-headshot.png";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.05 + i * 0.08, ease } }),
};

const Reveal = ({ i = 0, className = "", children, ...rest }) => (
  <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: "-60px" }} className={className} {...rest}>
    {children}
  </motion.div>
);

const CREDENTIALS = [
  ["Education", ["MA Counselling Psychology — Yorkville University, 2024", "Honours BASc, Human Behaviour — McMaster University, 2021", "BA, Psychology (Minor in Community Engagement) — McMaster University, 2021", "Both undergraduate degrees completed concurrently"]],
  ["Training & Certifications", ["Gottman Method Couples Therapy", "Cognitive Processing Therapy (CPT)", "Play Therapy", "Applied Behaviour Analysis (ABA)", "Gender-Based Violence Training", "LivingWorks safeTALK"]],
  ["Experience", ["5+ years with children, teens & families, including home-based work", "1 year at a rape crisis centre — sexual violence, domestic abuse & PTSD"]],
  ["Registration & Languages", ["Registered Psychotherapist (Qualifying) — CRPO", "Sessions in English & Urdu", "Virtual across Ontario · In-person in Milton & Burlington"]],
];

const Bio = () => (
  <section data-testid="about-bio" className="px-6 md:px-14 pt-14 md:pt-20 pb-20 md:pb-28">
    <motion.a variants={fadeUp} initial="hidden" animate="visible" custom={0} href="/" className="inline-flex items-center gap-2 text-sm text-[#2c1f20]/60 hover:text-[#6e4550] transition-colors">
      <ArrowLeft size={14} /> Back home
    </motion.a>
    <div className="grid md:grid-cols-12 gap-12 items-start mt-10">
      <div className="md:col-span-7">
        <Reveal i={1}><p className="eyebrow">About the practice</p></Reveal>
        <Reveal i={2}>
          <h1 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">
            Momna Sajid, <em className="text-[#6e4550]">RP (Qualifying).</em>
          </h1>
          <p className="mt-5 text-sm font-semibold tracking-[0.18em] uppercase text-[#2c1f20]/70">
            Founder &amp; Clinical Lead · Haya Healing Psychotherapy
          </p>
        </Reveal>
        <Reveal i={3}>
          <div className="mt-6 space-y-4 text-[#2c1f20]/75 leading-relaxed">
            <p>
              Haya Healing Psychotherapy is a clinical practice serving individuals, couples, families, children,
              and adolescents in Milton, Burlington, and online across Ontario — with a particular focus on South
              Asian couples and families, and women's mental health. Care here is evidence-based, trauma-informed,
              culturally responsive, and collaborative.
            </p>
            <p>
              The practice was founded by Momna Sajid, a Registered Psychotherapist (Qualifying) with the College
              of Registered Psychotherapists of Ontario (CRPO), offering therapy in English and Urdu. Her style is
              warm, authentic, and collaborative — drawing from Cognitive Behavioural Therapy (CBT), Acceptance
              and Commitment Therapy (ACT), Dialectical Behaviour Therapy (DBT), Cognitive Processing Therapy
              (CPT), Solution-Focused Therapy, and the Gottman Method for couples.
            </p>
            <p>
              Her path to this work began with children and families: more than five years of support work,
              beginning as an ABA therapist working one-to-one with autistic children. That discipline still
              shapes the practice — therapy follows a plan built together with each client, and if something isn't
              working, the plan changes.
            </p>
            <p>
              With couples, the practice draws on the Gottman Method — one of the most researched approaches to
              couples therapy. Many of the couples seen at Haya Healing Psychotherapy are navigating in-law involvement,
              extended family expectations, and community pressures neither partner chose — so sessions get
              specific, and the hard conversations get practised, in English or Urdu.
            </p>
            <p>
              The practice also brings deep trauma experience, including a year supporting adolescents and adults
              at a rape crisis centre. Clients commonly come to Haya Healing Psychotherapy for anxiety, depression, trauma and
              PTSD, relationship difficulties, life transitions, prenatal and postpartum mental health, and
              cultural identity concerns. In-person sessions are held at Halton Medix Walk-In Clinic in Milton and
              Burlington, with secure virtual therapy available across Ontario — and every new client begins with
              a complimentary 15-minute consultation.
            </p>
          </div>
        </Reveal>
        <Reveal i={4}>
          <p className="mt-7 font-serif-display italic text-2xl text-[#6e4550] leading-snug">
            "Your story is honoured here. Your pace is respected."
          </p>
        </Reveal>
        <Reveal i={5} className="mt-8">
          <a data-testid="about-book-button" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book an Initial Consultation</a>
        </Reveal>
      </div>
      <div className="md:col-span-5 space-y-4">
        <Reveal i={1}>
          <div className="rounded-md overflow-hidden border border-[#2c1f20]/10 bg-white shadow-[0_16px_40px_-24px_rgba(44,31,32,0.3)]">
            <img data-testid="about-headshot" src={HEADSHOT} alt="Momna Sajid – Registered Psychotherapist (Qualifying)" className="w-full h-[420px] object-contain bg-[#f1ece4]" />
            <div className="p-5 border-t border-[#2c1f20]/10">
              <p className="font-semibold text-sm">Momna Sajid, RP (Qualifying)</p>
              <p className="text-xs text-[#2c1f20]/60 mt-0.5">Registered Psychotherapist (Qualifying) · CRPO</p>
              <p className="text-xs text-[#2c1f20]/60 mt-0.5">English &amp; Urdu · In-person in Milton &amp; Burlington · Virtual across Ontario</p>
            </div>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="about-credentials">
          {CREDENTIALS.map(([t, items], i) => (
            <Reveal key={t} i={i + 2} className="bg-white/70 border border-[#2c1f20]/10 rounded-md p-5 transition-[box-shadow,border-color] duration-300 hover:border-[#6e4550]/40 hover:shadow-[0_16px_40px_-28px_rgba(44,31,32,0.3)]">
              <p className="text-xs font-semibold tracking-[0.12em] uppercase text-[#2c1f20]">{t}</p>
              <ul className="mt-3 space-y-2">
                {items.map((it) => (
                  <li key={it} className="text-xs text-[#2c1f20]/65 leading-relaxed flex gap-2">
                    <Check size={12} className="mt-0.5 shrink-0 text-[#6e4550]" />{it}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const WHO = [
  { icon: Flower2, title: "Women", desc: "Identity, self-esteem, burnout, motherhood, and the quiet weight of changing life roles.", chips: ["Self-esteem", "Burnout", "Identity", "Motherhood", "Life roles"] },
  { icon: Heart, title: "Couples", desc: "Strengthening communication, rebuilding emotional safety, and deepening connection.", chips: ["Communication", "Repair", "Intimacy"] },
  { icon: Home, title: "Families", desc: "Improving relationships, navigating conflict, and creating space for each voice to land.", chips: ["Conflict", "Boundaries", "Reconnection"] },
  { icon: Sprout, title: "Children & Teens", desc: "Anxiety, emotional regulation, behavioural concerns, ADHD, autism, and social challenges.", chips: ["Anxiety", "Emotional regulation", "ADHD", "Autism", "Social"] },
];

const WhoFor = () => (
  <section data-testid="about-who-for" className="px-6 md:px-14 py-28 md:py-40 border-t border-[#2c1f20]/10">
    <Reveal><p className="eyebrow">Who this work is for</p></Reveal>
    <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-4 max-w-2xl">We work with the people <em className="text-[#6e4550]">the world expects to hold it all together.</em></h2></Reveal>
    <Reveal i={2}>
      <p className="mt-5 max-w-2xl text-[#2c1f20]/75 leading-relaxed">
        We work with women, couples, families, children, and adolescents navigating anxiety, relationship
        difficulties, life transitions, and the weight of daily expectations—with a particular focus on
        women's mental health.
      </p>
    </Reveal>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
      {WHO.map((w, i) => (
        <Reveal key={w.title} i={i + 1} className="card">
          <div className="w-10 h-10 rounded-full bg-[#f4e6e2] flex items-center justify-center text-[#8a5a63]"><w.icon size={18} /></div>
          <h3 className="font-serif-display text-2xl mt-4">{w.title}</h3>
          <p className="text-sm text-[#2c1f20]/70 mt-2 leading-relaxed">{w.desc}</p>
          <div className="flex flex-wrap gap-1.5 mt-4">{w.chips.map((c) => <span key={c} className="chip">{c}</span>)}</div>
        </Reveal>
      ))}
    </div>
  </section>
);

const PersonalNote = () => (
  <section className="px-6 md:px-14 py-28 md:py-40 border-t border-[#2c1f20]/10">
    <div className="max-w-3xl">
    <Reveal><p className="eyebrow">From our founder</p></Reveal>
    <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4">Why this practice <em className="text-[#6e4550]">exists.</em></h2></Reveal>
    <Reveal i={2}>
      <p className="mt-8 text-[#2c1f20]/75 leading-relaxed">
        "I became a psychotherapist because I've always been fascinated by human behaviour—understanding why people
        think, feel, and respond the way they do. Every person has a unique story, and I believe our experiences
        shape the way we see ourselves, others, and the world.
      </p>
    </Reveal>
    <Reveal i={3}>
      <p className="mt-6 text-[#2c1f20]/75 leading-relaxed">
        Growing up as the eldest daughter in an immigrant household, and now as a mother, has given me a deep
        appreciation for identity, cultural expectations, and balancing multiple worlds. Those experiences shaped
        the standard of care Haya Healing Psychotherapy was built on: empathy, cultural humility, and clinical rigour."
      </p>
    </Reveal>
    <Reveal i={4}>
      <p className="mt-10 font-serif-display italic text-xl md:text-2xl text-[#6e4550] leading-snug">
        "Compassion, curiosity, and a steady presence through life's uncertainties."
      </p>
      <p className="mt-3 text-xs tracking-[0.25em] uppercase text-[#2c1f20]/50">— Momna Sajid, Founder</p>
    </Reveal>
    </div>
  </section>
);

const Cta = () => (
  <footer className="px-6 md:px-14 py-16 md:py-24 border-t border-[#2c1f20]/10 bg-[#f1ece4]/50">
    <Reveal><p className="eyebrow">Begin here</p></Reveal>
    <Reveal i={1}>
      <h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl leading-tight">
        Book an <em className="text-[#6e4550]">Initial Consultation.</em>
      </h2>
    </Reveal>
    <Reveal i={2} className="mt-8 flex flex-wrap gap-6 items-center">
      <a data-testid="about-footer-book" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book via Jane App <ArrowUpRight size={15} /></a>
      <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Mail size={15} /> momna@hayahealingpsychotherapy.com</a>
      <a href="tel:+13656172135" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Phone size={15} /> (365) 617-2135</a>
    </Reveal>
    <p className="mt-14 text-xs text-[#2c1f20]/40 flex items-center gap-2"><MapPin size={12} /> Milton · Burlington · Virtual across Ontario</p>
        <p className="mt-4 text-[8px] leading-relaxed text-[#2c1f20]/30">Some content on this site was created with the assistance of AI tools and reviewed by Haya Healing Psychotherapy.</p>
  </footer>
);

export default function About() {
  useEffect(() => {
    document.title = "About Momna Sajid, RP(Q) | Psychotherapist in Milton & Burlington";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Momna Sajid, Registered Psychotherapist (Qualifying) — MA Counselling Psychology, Gottman Method trained. Therapy in English & Urdu in Milton, Burlington, and online across Ontario.");
  }, []);
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <Bio />
      <WhoFor />
      <PersonalNote />
      <Cta />
    </div>
  );
}
